<script setup>
import { computed, onMounted, onUpdated, ref, watch } from "vue";
import { useModalogStore } from "@/stores/modalog";
import { useToastStore } from "@/stores/toast.js";
import { range } from "lodash";
import { router } from "@inertiajs/vue3";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";
import IconHint from "~/svg/icon-hint.svg";
import IconExternalLink from "~/svg/icon-external-link.svg";

// Stores
const modalogStore = useModalogStore();
const toastStore = useToastStore();

// Props
const props = defineProps({
    item: {
        type: Object,
        default: () => ({}),
    },
    isUnconfirm: {
        type: Boolean,
        default: false,
    },
});

// State
const submitting = ref(false);
const qtyConfirming = ref(0);
const showHint = ref(false);

// Hooks
onMounted(() => {
    setQtyConfirming();
});

// The host keeps this instance mounted between opens (stable-root
// pattern), so per-open state re-seeds on every open (GH-955).
watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            showHint.value = false;
            setQtyConfirming();
        }
    },
);

onUpdated(() => {
    setQtyConfirming();
});

// Computed
const isSingleItem = computed(() => {
    return props.item.qty_desired === 1;
});

const isSingleUnconfirm = computed(() => {
    return props.isUnconfirm && isSingleItem.value;
});

const isQtySelectDisabled = computed(() => {
    return !props.item.qty_purchased && qtyRemaining.value === 1;
});

/**
 * This looks insane, but it's really here for a good reason.
 * @see GiftConfirmationController.php
 * We're trying to reasonably calculate the number of items a user should
 * be able to confirm as purchased.
 */
const qtyConfirmable = computed(() => {
    // If I purchased 0 items,
    // the dropdown should be from 1 to item.qty_remaining
    if (props.item.qty_purchased === 0) {
        return range(1, qtyRemaining.value + 1); // zero-indexed
    } else {
        // If I purchased 1 or more
        // And the qty_remaining === 0
        // Then, the range is 0 to item.qty_purchased
        if (qtyRemaining.value === 0) {
            return range(0, props.item.qty_purchased + 1); // zero-indexed
        } else {
            // If the number of items remaining is more than 0,
            // then the dropdown should be from 0 to the number of items I purchased + the number
            // of items still remaining.
            return range(0, props.item.qty_purchased + qtyRemaining.value + 1); // zero-indexed
        }
    }
});

const qtyRemaining = computed(() => {
    return props.item.qty_remaining;
});

const retailer = computed(() => {
    return props.item.product.retailer;
});

const retailerName = computed(() => {
    return retailer.value.name !== "custom_item"
        ? retailer.value.name
            ? retailer.value.name
            : retailer.value.hostname
        : false;
});

// Methods
function submitPurchase(qty) {
    submitting.value = true;

    axios
        .post(route("gifts.confirm", { item: props.item.uuid }), {
            qty,
        })
        .then(() => {
            toastStore.success(
                qty === 0
                    ? `"${props.item.name}" was marked as available again.`
                    : `"${props.item.name}" was confirmed as purchased.`,
            );
        })
        .catch((error) => {
            console.log(error);
        })
        .finally(() => {
            submitting.value = false;
            modalogStore.closeModalog();
            router.reload({ only: ["item"] });
            setQtyConfirming();
        });
}

function handleConfirm() {
    submitPurchase(isSingleUnconfirm.value ? 0 : qtyConfirming.value);
}

/**
 * Reset the `qtyConfirming` state
 */
function setQtyConfirming() {
    qtyConfirming.value = Number(
        props.item.qty_purchased === 0 ? 1 : props.item.qty_purchased,
    );
}

const qtySelectClasses =
    "h-11 w-[72px] appearance-none rounded-[5px] border border-solid border-[#cdc4c2] bg-white bg-none py-0 pl-4 pr-9 font-brand text-[15px] text-[#1d2333] focus:border-brand-navy focus:outline-none disabled:opacity-50";
</script>

<template>
    <BrandConfirmModalog
        :title="
            isSingleUnconfirm ? 'Unconfirm Gift?' : 'Did You Buy This Gift?'
        "
        :confirm-label="isSingleUnconfirm ? 'Unconfirm' : 'Confirm'"
        :busy-label="isSingleUnconfirm ? 'Unconfirming' : 'Confirming'"
        :danger="isSingleUnconfirm"
        :busy="submitting"
        @confirm="handleConfirm"
    >
        <!-- Gift summary -->
        <div class="flex items-center gap-4">
            <img
                :src="item.image_url.img_1x"
                :alt="item.name"
                class="size-[70px] shrink-0 rounded-[5px] border border-solid border-[#e6dfdd] bg-white object-cover"
            />
            <div class="min-w-0">
                <p
                    class="line-clamp-2 text-[15px] font-bold leading-snug text-brand-navy"
                >
                    {{ item.name }}
                </p>
                <!--@todo MR: Currency-->
                <p class="mt-0.5 text-[15px] text-[#3c3938]">
                    ${{ item.price }}
                </p>
                <p v-if="retailerName" class="text-[13px] text-[#88959e]">
                    {{ retailerName }}
                </p>
            </div>
        </div>

        <!-- Quantity -->
        <div v-if="!isSingleItem" class="mt-5">
            <p class="text-[15px] font-bold text-[#3c3938]">
                How many did you buy?
            </p>
            <div class="mt-2 flex items-center gap-3">
                <label for="quantity-purchased" class="sr-only"
                    >Quantity purchased</label
                >
                <div class="relative">
                    <select
                        name="quantity-purchased"
                        id="quantity-purchased"
                        :disabled="isQtySelectDisabled"
                        v-model="qtyConfirming"
                        :class="qtySelectClasses"
                    >
                        <option
                            v-for="n in qtyConfirmable"
                            :key="n"
                            :value="n"
                            v-text="n"
                        ></option>
                    </select>
                    <svg
                        viewBox="0 0 13 10"
                        class="pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-[13px] -translate-y-1/2 text-brand-red"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M6.5 10L0 0h13L6.5 10z" />
                    </svg>
                </div>
                <p class="text-[15px] text-[#3c3938]">
                    of <strong>{{ item.qty_desired }} requested</strong>.
                </p>
            </div>
        </div>

        <!-- How-it-works hint -->
        <div class="mt-6 border-t border-solid border-black/10 pt-4">
            <button
                type="button"
                class="inline-flex items-center gap-2 text-[14px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
                @click="showHint = !showHint"
            >
                <IconHint class="size-4 shrink-0" aria-hidden="true" />
                {{
                    isSingleUnconfirm
                        ? "What does unconfirming do?"
                        : "How does confirming gifts work?"
                }}
            </button>
            <div
                v-show="showHint"
                class="mt-3 rounded-[5px] bg-brand-cream/60 p-4 text-[14px] leading-relaxed text-[#3c3938]"
            >
                <template v-if="isSingleUnconfirm">
                    Unconfirming will mark this gift as available again so
                    others know it can still be purchased.
                </template>
                <template v-else>
                    Confirming a gift will let other people viewing the wish
                    list know that the gift was already purchased. We won't
                    spoil it for the gift requester.
                </template>
                <a
                    href="https://support.gifthero.com/page/confirming-gifts/"
                    target="_blank"
                    class="ml-1 inline-flex items-center gap-1 whitespace-nowrap font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
                    >Learn more
                    <IconExternalLink
                        class="size-3 shrink-0"
                        aria-hidden="true"
                /></a>
            </div>
        </div>
    </BrandConfirmModalog>
</template>
